/**
 * Format price regarding the locale that is sent with it.
 *
 * @param {number} price - The price in EUR
 * @param {string} locale - The locale code (e.g., "nl-BE") used for formatting
 * @returns {string} The formatted currency string
 */
const formatPrice = (price: number, locale: string) => {
  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency: 'EUR',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(price);
};

/**
 * Multiply a unit price by a quantity to get the line total.
 *
 * Note: `quantity * price` can drift on floats the same way `num * 100` does
 * (see floatToCentsSafely). Acceptable here because the result is rendered to
 * 2 decimals downstream, so sub-cent drift is rounded away before display.
 *
 * @param {number} price - The unit price in EUR
 * @param {number | undefined | null} quantity - The quantity (defaults to 1)
 * @returns {number} The total price for the given quantity
 */
const calculatePriceByQuantity = (price: number, quantity: number | undefined | null = 1) => {
  if (!quantity) {
    return price;
  }
  return quantity * price;
};

/**
 * Calculate unit price from total price and quantity with proper floating-point rounding.
 * Uses toFixed(2) to avoid floating-point arithmetic issues (e.g., 73.96 / 2 = 36.980000000000004).
 * This matches the rounding logic used in payment processing.
 *
 * @param {number | undefined} totalPrice - The total price for all items
 * @param {number | undefined} quantity - The quantity of items
 * @returns {number | undefined} The unit price rounded to 2 decimal places, or undefined if totalPrice is undefined
 */
const getUnitPrice = (totalPrice: number | undefined, quantity: number | undefined): number | undefined => {
  if (totalPrice === undefined || !quantity) {
    return totalPrice;
  }
  return Number((totalPrice / quantity).toFixed(2));
};

/**
 * Convert a EUR float to integer cents for Pay.nl, without floating-point drift.
 *
 * Why not `Math.round(num * 100)`: in JS, 73.96 * 100 = 7395.999999999999, and at
 * larger amounts the multiplication drifts far enough that rounding lands on the
 * wrong cent. Reading the cents off the string form (toFixed + drop the decimal)
 * gives Pay.nl the exact value we intended, regardless of amount size.
 *
 * Returns 0 if the input is undefined, null, or NaN.
 *
 * @param {number | undefined | null} num - The price in EUR (e.g., 73.96)
 * @returns {number} The price in cents as an integer (e.g., 7396)
 */
const floatToCentsSafely = (num: number | undefined | null): number => {
  if (num === undefined || num === null || isNaN(num)) {
    return 0;
  }
  return parseInt(num.toFixed(2).replace('.', ''));
};

/**
 * Convert integer cents (Pay.nl) back to a EUR float, the inverse of floatToCentsSafely.
 *
 * @param {number | undefined | null} num - The price in cents as an integer (e.g., 7396)
 * @returns {number} The price in EUR (e.g., 73.96), or 0 if the input is undefined, null, or NaN
 */
const centsToFloatSafely = (num: number | undefined | null): number => {
  if (num === undefined || num === null || isNaN(num)) {
    return 0;
  }
  const str = num.toString();
  const padded = str.padStart(3, '0');
  const cents = padded.slice(0, -2);
  const fraction = padded.slice(-2);

  return Number(`${cents}.${fraction}`);
};

export { formatPrice, calculatePriceByQuantity, getUnitPrice, floatToCentsSafely, centsToFloatSafely };
